/**
 * Çekilişler (/admin/cekilisler).
 *
 * Sayfanın iki katmanı vardır ve ayrımı yönetici için önemlidir: **sayfa
 * anahtarı** oyuncudaki `/cekilis` bölümünü tümden açar/kapatır (menüden de
 * düşer), **çekilişin durumu** ise o tek kampanyanın yayında olup olmadığını
 * söyler. İkisi birlikte sağlanmadan oyuncu hiçbir şey görmez; bu yüzden
 * yayın kartı ve uyarı bantları en üstte durur.
 *
 * Görsel dil Çapa Etkinliği sayfasınınkiyle aynıdır (raffle.css / anchor.css).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ticket, Plus, Edit3, Trash2, X, Loader2, Search, RefreshCw, Trophy,
  Users, Gift, Coins, Package, Sparkles, CalendarClock, ShieldCheck,
  ArrowLeft, Dices, Repeat2, CheckCircle2, ChevronLeft, ChevronRight,
  ImagePlus, Eye, EyeOff, AlertTriangle, Info, Wallet, Timer,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import { remainingText } from '../lib/raffle';
import './raffle.css';

const emptyRaffle = {
  title: '',
  description: '',
  image_url: '',
  status: 'draft',
  starts_at: '',
  ends_at: '',
  draw_at: '',
  ticket_amount: 500,
  max_tickets_per_user: 0,
  allow_multiple_wins: false,
  balance_counts: false,
};

const emptyPrize = {
  title: '',
  reward_type: 'cash',
  amount: 0,
  wager_multiplier: 0,
  winner_count: 1,
  order: 0,
};

// Yalnızca varsayılan: boyutu yönetici seçer (bkz. lib/pagination).
const PAGE_SIZE = 25;

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const compact = (value) => new Intl.NumberFormat('tr-TR').format(Number(value || 0));

const formatDate = (value) => (value ? new Date(value).toLocaleString('tr-TR') : '—');

const formatDay = (value) => (
  value
    ? new Date(value).toLocaleDateString('tr-TR', {
      day: '2-digit', month: 'short', year: '2-digit',
    })
    : '—'
);

// datetime-local alanı yerel saat bekler, toISOString() ise UTC verir. Kaydetme
// yolu girilen değeri yerel saat kabul ettiği için doldururken de yerele
// çevirmezsek tarih her kayıtta UTC farkı kadar geri kayar.
const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toPayloadDate = (value) => (value ? new Date(value).toISOString() : null);

const STATUS_LABELS = {
  draft: 'Taslak',
  active: 'Yayında',
  closed: 'Bilet kapalı',
  drawn: 'Kura çekildi',
};

const STATUS_TONES = {
  draft: 'is-muted',
  active: 'is-ok',
  closed: 'is-warn',
  drawn: 'is-info',
};

const PRIZE_LABELS = {
  cash: 'Nakit',
  bonus: 'Bonus',
  custom: 'Fiziksel ödül',
};

const PRIZE_ICONS = { cash: Coins, bonus: Sparkles, custom: Package };

const Raffles = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pageEnabled, setPageEnabled] = useState(false);
  const [publicActive, setPublicActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyRaffle);
  const [busy, setBusy] = useState(false);
  const [togglingPage, setTogglingPage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { toast } = useToast();

  // Görsel panelden yüklenir; dönen adres görelidir (/api/uploads/...) ve
  // çizilirken resolveApiAssetUrl()'den geçirilir — ayrı origin'li kurulumda
  // çözülmeyen adres 404 olurdu.
  const uploadImage = async (file) => {
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({
        title: 'Görsel yüklenemedi',
        description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.`,
      });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploadingImage(true);
    try {
      const { data } = await client.post('/admin/uploads/images', formData);
      setForm((current) => ({ ...current, image_url: data.url }));
      toast({ title: 'Görsel yüklendi' });
    } catch (requestError) {
      toast({
        title: 'Yükleme başarısız',
        description: requestError.response?.data?.detail || 'Görsel yüklenemedi',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/raffles');
      setItems(data.items || []);
      setSummary(data.summary || null);
      setPageEnabled(data.enabled === true);
      setPublicActive(data.public_active === true);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Çekilişler alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePage = async () => {
    const next = !pageEnabled;
    setTogglingPage(true);
    try {
      const { data } = await client.patch('/admin/raffles/settings', {
        enabled: next,
      });
      setPageEnabled(data.enabled === true);
      setPublicActive(data.public_active === true);
      toast({
        title: next ? 'Çekiliş sayfası yayında' : 'Çekiliş sayfası kapatıldı',
        description: next
          ? 'Oyuncular Çekiliş bölümünü menüde görebilir.'
          : 'Bölüm menüden düştü ve adresi girenler de göremez.',
      });
    } catch (requestError) {
      toast({
        title: 'Anahtar değiştirilemedi',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setTogglingPage(false);
    }
  };

  const openNew = () => {
    setForm(emptyRaffle);
    setModal({ mode: 'new' });
  };

  const openEdit = (item) => {
    setForm({
      ...emptyRaffle,
      ...item,
      description: item.description || '',
      image_url: item.image_url || '',
      starts_at: toInputDate(item.starts_at),
      ends_at: toInputDate(item.ends_at),
      draw_at: toInputDate(item.draw_at),
    });
    setModal({ mode: 'edit', id: item.id });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || '',
        image_url: form.image_url || '',
        status: form.status,
        starts_at: toPayloadDate(form.starts_at),
        ends_at: toPayloadDate(form.ends_at),
        draw_at: toPayloadDate(form.draw_at),
        ticket_amount: Number(form.ticket_amount || 0),
        max_tickets_per_user: Number(form.max_tickets_per_user || 0),
        allow_multiple_wins: !!form.allow_multiple_wins,
        balance_counts: !!form.balance_counts,
      };
      if (modal.mode === 'new') {
        if (!payload.starts_at || !payload.ends_at) {
          toast({
            title: 'Tarih aralığı zorunlu',
            description: 'Bilet kazandıran dönemin başlangıcı ve bitişi girilmeli.',
          });
          setBusy(false);
          return;
        }
        await client.post('/admin/raffles', payload);
      } else {
        await client.patch(`/admin/raffles/${modal.id}`, payload);
      }
      setModal(null);
      await load();
      toast({ title: 'Çekiliş kaydedildi' });
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(
      `${item.title} silinsin mi? Verilmiş biletler ve ödüller de silinir.`)) return;
    try {
      await client.delete(`/admin/raffles/${item.id}`);
      if (selectedId === item.id) setSelectedId(null);
      await load();
      toast({ title: 'Çekiliş silindi' });
    } catch (requestError) {
      toast({
        title: 'Silinemedi',
        description: requestError.response?.data?.detail,
      });
    }
  };

  const liveCount = summary?.active ?? items.filter(
    (item) => item.status === 'active').length;
  const pendingDeliveries = summary?.pending_deliveries ?? 0;

  if (selectedId) {
    return (
      <RaffleDetail
        raffleId={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
        onEdit={openEdit}
      />
    );
  }

  return (
    <div className="rfl-page">
      <header className="rfl-head">
        <span className="rfl-head-icon"><Ticket size={21} /></span>
        <div className="rfl-head-text">
          <h1>
            Çekilişler
            <span className={`rfl-pill ${pageEnabled ? 'is-ok' : 'is-muted'}`}>
              {pageEnabled ? <Eye size={11} /> : <EyeOff size={11} />}
              {pageEnabled ? 'Sayfa açık' : 'Sayfa kapalı'}
            </span>
          </h1>
          <p>
            Yatırımdan bilet kazandıran kampanyalar. Bilet ayrı bir sayaçta
            tutulmaz: üyenin dönem içindeki onaylı yatırım toplamından türer ve
            kurayı siz çekersiniz.
          </p>
        </div>
        <div className="rfl-head-actions">
          <button type="button" onClick={load} className="admin-btn-secondary">
            <RefreshCw size={14} /> Yenile
          </button>
          <button type="button" onClick={openNew} className="admin-btn-primary">
            <Plus size={14} /> Yeni Çekiliş
          </button>
        </div>
      </header>

      <section className={`rfl-live ${pageEnabled ? 'is-on' : ''}`}>
        <span className="rfl-live-mark">
          {pageEnabled ? <Eye size={19} /> : <EyeOff size={19} />}
        </span>
        <div className="rfl-live-text">
          <div className="rfl-live-title">
            {pageEnabled ? 'Çekiliş sayfası yayında' : 'Çekiliş sayfası kapalı'}
            <span className={`rfl-pill ${publicActive ? 'is-ok' : 'is-muted'}`}>
              {publicActive ? 'Menüde görünüyor' : 'Menüde yok'}
            </span>
          </div>
          <p>
            {pageEnabled
              ? 'Oyuncular /cekilis bölümünü açabilir; menüdeki bağlantı yayında '
                + 'bir çekiliş varken çizilir.'
              : 'Bölüm bütün temalarda menüden düşer ve adresi elle girenler de '
                + 'göremez. Kayıtlı çekilişler silinmez, burada bekler.'}
          </p>
        </div>
        <button
          type="button"
          className="rfl-live-btn"
          onClick={togglePage}
          disabled={togglingPage}
        >
          {togglingPage
            ? <Loader2 size={15} className="animate-spin" />
            : (pageEnabled ? <EyeOff size={15} /> : <Eye size={15} />)}
          {pageEnabled ? 'Sayfayı kapat' : 'Sayfayı yayına al'}
        </button>
      </section>

      {pageEnabled && liveCount === 0 && (
        <div className="rfl-note is-warn">
          <AlertTriangle size={15} />
          <span className="rfl-note-body">
            Sayfa açık ama <b>yayında çekiliş yok</b>. Bölüm menüde
            görünmüyor; bir çekilişin durumunu <b>Yayında</b> yapın.
          </span>
        </div>
      )}

      {!pageEnabled && liveCount > 0 && (
        <div className="rfl-note is-warn">
          <AlertTriangle size={15} />
          <span className="rfl-note-body">
            <b>{liveCount} çekiliş yayında</b> ama sayfa kapalı: oyuncular
            göremiyor ve <b>yeni bilet kazanmıyor</b>. Kapalı kaldığı sürece
            yatırımlar bilete çevrilmez.
          </span>
        </div>
      )}

      {pendingDeliveries > 0 && (
        <div className="rfl-note is-info">
          <Info size={15} />
          <span className="rfl-note-body">
            <b>{pendingDeliveries} fiziksel ödül</b> teslim bekliyor. Kazananın
            kartındaki <b>Kazananlar</b> sekmesinden işaretleyin.
          </span>
        </div>
      )}

      <div className="rfl-stats">
        <Stat
          icon={Trophy}
          tone="ok"
          label="Yayında"
          value={compact(liveCount)}
          hint={`Toplam ${compact(summary?.total ?? items.length)} çekiliş`}
        />
        <Stat
          icon={Ticket}
          tone="gold"
          label="Yayındaki bilet"
          value={compact(summary?.live_tickets ?? 0)}
          hint={`Tüm zamanlar ${compact(summary?.tickets ?? 0)}`}
        />
        <Stat
          icon={Wallet}
          tone="info"
          label="Ödül havuzu"
          value={money(summary?.live_pool ?? 0)}
          hint="Yayındaki çekilişlerin para ödülleri"
        />
        <Stat
          icon={Package}
          tone={pendingDeliveries > 0 ? 'warn' : 'neutral'}
          label="Teslim bekleyen"
          value={compact(pendingDeliveries)}
          hint="Fiziksel ödüller"
        />
      </div>

      {loading ? (
        <div className="rfl-skeleton">
          <span /><span /><span />
        </div>
      ) : error ? (
        <div className="rfl-panel">
          <div className="rfl-state">
            <AlertTriangle size={26} />
            <h2>Çekilişler alınamadı</h2>
            <p>{error}</p>
            <button type="button" onClick={load} className="admin-btn-secondary">
              <RefreshCw size={14} /> Yeniden dene
            </button>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="rfl-panel">
          <div className="rfl-state">
            <Ticket size={26} />
            <h2>Henüz çekiliş oluşturulmadı</h2>
            <p>
              Bir çekiliş açın, bilet tutarını ve dönemi belirleyin; üyeler
              dönem içindeki her yatırımlarıyla bilet kazanmaya başlasın.
            </p>
            <button type="button" onClick={openNew} className="admin-btn-primary">
              <Plus size={14} /> Yeni Çekiliş
            </button>
          </div>
        </div>
      ) : (
        <div className="rfl-grid">
          {items.map((item) => (
            <RaffleCard
              key={item.id}
              item={item}
              onOpen={() => setSelectedId(item.id)}
              onEdit={() => openEdit(item)}
              onRemove={() => remove(item)}
            />
          ))}
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => !busy && setModal(null)}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div
            className="relative admin-card admin-modal w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><Ticket size={19} /></div>
              <div className="min-w-0">
                <h2 className="font-black text-[15px] leading-tight">
                  {modal.mode === 'new' ? 'Yeni Çekiliş' : 'Çekilişi Düzenle'}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Bilet kuralını ve dönemi tanımlayın; ödüller bir sonraki adımda
                </p>
              </div>
              <button type="button" onClick={() => setModal(null)} className="icon-btn view ml-auto">
                <X size={14} />
              </button>
            </div>

            <div className="admin-modal-body">
              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><Sparkles size={12} /> Tanım</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Başlık" hint="Oyuncunun Çekiliş sayfasında göreceği ad.">
                      <input
                        value={form.title}
                        onChange={(event) => setForm({ ...form, title: event.target.value })}
                        className="admin-input"
                        placeholder="Yılbaşı Çekilişi"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Açıklama" hint="Kampanya koşullarını oyuncunun diliyle yazın.">
                      <textarea
                        value={form.description}
                        onChange={(event) => setForm({ ...form, description: event.target.value })}
                        className="admin-input"
                        rows={3}
                        placeholder="Her 500 ₺ yatırıma 1 bilet kazanırsınız."
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-xs text-slate-400 block mb-1.5">Çekiliş Görseli</span>
                    <div className="grid gap-3 sm:grid-cols-[170px_1fr]">
                      <div className="relative aspect-[16/9] overflow-hidden rounded-xl border border-white/10 bg-slate-900">
                        {form.image_url ? (
                          <img
                            src={resolveApiAssetUrl(form.image_url)}
                            alt="Çekiliş önizlemesi"
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 grid place-items-center text-slate-600">
                            <ImagePlus size={28} />
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label
                          className={`admin-btn-primary flex min-h-10 cursor-pointer items-center justify-center gap-2 ${
                            uploadingImage ? 'pointer-events-none opacity-60' : ''
                          }`}
                        >
                          {uploadingImage
                            ? <Loader2 size={15} className="animate-spin" />
                            : <ImagePlus size={15} />}
                          Görsel Seç
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                            className="sr-only"
                            disabled={uploadingImage}
                            onChange={(event) => {
                              uploadImage(event.target.files?.[0]);
                              event.target.value = '';
                            }}
                          />
                        </label>
                        <input
                          value={form.image_url}
                          onChange={(event) => setForm({
                            ...form, image_url: event.target.value,
                          })}
                          className="admin-input"
                          placeholder="veya görsel URL'si"
                        />
                        <p className="text-[11px] leading-relaxed text-slate-500">
                          Önerilen ölçü 16:9. En fazla {UPLOAD_MAX_LABEL}. Boş bırakılırsa
                          sayfa görselsiz çizilir.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><Ticket size={12} /> Bilet Kuralı</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="Bilet Tutarı"
                    hint="Bu tutardaki her yatırım bir bilet kazandırır. Artan tutar kaybolmaz, sonraki yatırımla toplanır."
                  >
                    <Affix text="₺">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={form.ticket_amount}
                        onChange={(event) => setForm({ ...form, ticket_amount: event.target.value })}
                        className="admin-input pr-10"
                      />
                    </Affix>
                  </Field>
                  <Field label="Üye Başına Bilet Tavanı" hint="0 = sınırsız">
                    <Affix text="adet">
                      <input
                        type="number"
                        min="0"
                        value={form.max_tickets_per_user}
                        onChange={(event) => setForm({
                          ...form, max_tickets_per_user: event.target.value,
                        })}
                        className="admin-input pr-10"
                      />
                    </Affix>
                  </Field>
                </div>
                <div className="mt-3">
                  <SwitchRow
                    icon={Wallet}
                    label="Mevcut bakiye de bilet kazandırsın"
                    description="Açıkken üye, çekiliş dönemindeki yatırımı ile hesabındaki bakiyesinden hangisi yüksekse onun karşılığı kadar bilet alır — ikisi toplanmaz, aynı para iki kez sayılmaz. Kapalıyken yalnızca onaylı para yatırma işlemleri sayılır."
                    checked={!!form.balance_counts}
                    onChange={(balance_counts) => setForm({ ...form, balance_counts })}
                  />
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><CalendarClock size={12} /> Dönem</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Başlangıç" hint="Bu andan önceki yatırımlar sayılmaz.">
                    <input
                      type="datetime-local"
                      value={form.starts_at}
                      onChange={(event) => setForm({ ...form, starts_at: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                  <Field label="Bitiş" hint="Bu andan sonra yeni bilet kazanılmaz.">
                    <input
                      type="datetime-local"
                      value={form.ends_at}
                      onChange={(event) => setForm({ ...form, ends_at: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                  <Field label="Kura Tarihi" hint="Yalnızca ilandır; kurayı siz başlatırsınız.">
                    <input
                      type="datetime-local"
                      value={form.draw_at}
                      onChange={(event) => setForm({ ...form, draw_at: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><ShieldCheck size={12} /> Durum ve Kurallar</h3>
                <div className="grid gap-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <StatusChoice
                      title="Taslak"
                      description="Oyuncuya görünmez"
                      active={form.status === 'draft'}
                      onSelect={() => setForm({ ...form, status: 'draft' })}
                    />
                    <StatusChoice
                      title="Yayında"
                      description="Bilet kazanılıyor"
                      active={form.status === 'active'}
                      onSelect={() => setForm({ ...form, status: 'active' })}
                    />
                    <StatusChoice
                      title="Bilet kapalı"
                      description="Liste donduruldu"
                      active={form.status === 'closed'}
                      onSelect={() => setForm({ ...form, status: 'closed' })}
                    />
                  </div>
                  <SwitchRow
                    icon={Repeat2}
                    label="Bir üye birden çok ödül kazanabilsin"
                    description="Kapalıyken kazanan üyenin kalan biletleri havuzdan düşer ve yalnızca tek ödül alır."
                    checked={!!form.allow_multiple_wins}
                    onChange={(allow_multiple_wins) => setForm({ ...form, allow_multiple_wins })}
                  />
                </div>
              </section>
            </div>

            <div className="admin-modal-foot">
              <span className="admin-modal-summary">
                <Ticket size={12} />
                <b className="truncate">{form.title.trim() || 'Çekiliş'}</b>
                <span className="opacity-50">→</span>
                {money(form.ticket_amount)} = 1 bilet
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setModal(null)}
                  disabled={busy}
                  className="admin-btn-secondary"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  disabled={busy || uploadingImage || !form.title.trim()}
                  onClick={save}
                  className="admin-btn-primary"
                >
                  {busy && <Loader2 size={14} className="animate-spin" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Liste kartı: görsel, durum, bilet kuralı, katılım ve ödül havuzu. */
const RaffleCard = ({ item, onOpen, onEdit, onRemove }) => {
  const drawn = item.status === 'drawn';
  const countdown = item.status === 'active' ? remainingText(item.ends_at) : '';
  return (
    <article className={`rfl-card ${item.status === 'draft' ? 'is-draft' : ''}`}>
      <div className="rfl-card-art">
        {item.image_url ? (
          <img src={resolveApiAssetUrl(item.image_url)} alt="" />
        ) : (
          <span className="rfl-card-art-empty"><Ticket size={38} /></span>
        )}
        <div className="rfl-card-badges">
          <span className={`rfl-pill ${STATUS_TONES[item.status] || 'is-muted'}`}>
            {STATUS_LABELS[item.status] || item.status}
          </span>
          {item.balance_counts && (
            <span className="rfl-pill is-info">
              <Wallet size={11} /> Bakiye sayılıyor
            </span>
          )}
          {item.pending_deliveries > 0 && (
            <span className="rfl-pill is-warn">
              <Package size={11} /> {item.pending_deliveries} teslim
            </span>
          )}
        </div>
      </div>

      <div className="rfl-card-body">
        <button type="button" className="rfl-card-title" onClick={onOpen}>
          {item.title}
        </button>
        {item.description && <p className="rfl-card-desc">{item.description}</p>}

        <div className="rfl-card-facts">
          <div className="rfl-fact is-gold">
            <span className="rfl-fact-label">Bilet kuralı</span>
            <span className="rfl-fact-value">{money(item.ticket_amount)} = 1</span>
          </div>
          <div className="rfl-fact">
            <span className="rfl-fact-label">Katılım</span>
            <span className="rfl-fact-value">
              {compact(item.ticket_count)} bilet · {compact(item.participant_count)} üye
            </span>
          </div>
          <div className="rfl-fact is-ok">
            <span className="rfl-fact-label">Ödül havuzu</span>
            <span className="rfl-fact-value">{money(item.prize_pool)}</span>
          </div>
          <div className="rfl-fact">
            <span className="rfl-fact-label">{drawn ? 'Kazanan' : 'Ödül hakkı'}</span>
            <span className="rfl-fact-value">
              {drawn
                ? `${compact(item.winner_count)} kişi`
                : `${compact(item.winner_slots)} kişi · ${compact(item.prize_count)} ödül`}
            </span>
          </div>
        </div>

        <div className="rfl-card-window">
          <CalendarClock size={13} />
          {formatDay(item.starts_at)} – {formatDay(item.ends_at)}
          {countdown && (
            <span className="rfl-pill is-info"><Timer size={11} /> {countdown}</span>
          )}
        </div>
      </div>

      <div className="rfl-card-foot">
        <button type="button" className="rfl-mini is-primary is-grow" onClick={onOpen}>
          <Trophy size={13} /> Yönet
        </button>
        <button
          type="button"
          className="rfl-mini is-icon"
          onClick={onEdit}
          disabled={drawn}
          title={drawn ? 'Kurası çekilmiş çekiliş değiştirilemez' : 'Düzenle'}
        >
          <Edit3 size={13} />
        </button>
        <button
          type="button"
          className="rfl-mini is-icon is-danger"
          onClick={onRemove}
          disabled={drawn}
          title={drawn ? 'Kurası çekilmiş çekiliş silinemez' : 'Sil'}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </article>
  );
};

/** Tek çekilişin yönetimi: ödüller, katılımcılar, kura ve kazananlar. */
const RaffleDetail = ({ raffleId, onBack, onEdit }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [participants, setParticipants] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(
    () => readPageSize('raffle-participants', PAGE_SIZE));
  const [tab, setTab] = useState('prizes');
  const [busy, setBusy] = useState('');
  const [prizeModal, setPrizeModal] = useState(null);
  const [prizeForm, setPrizeForm] = useState(emptyPrize);
  const [grant, setGrant] = useState({ username: '', count: 1, note: '' });
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get(`/admin/raffles/${raffleId}`);
      setDetail(data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Çekiliş alınamadı');
    } finally {
      setLoading(false);
    }
  }, [raffleId]);

  const loadParticipants = useCallback(async () => {
    try {
      const { data } = await client.get(`/admin/raffles/${raffleId}/participants`, {
        params: { search, skip: page * pageSize, limit: pageSize },
      });
      setParticipants(data);
    } catch {
      setParticipants({ items: [], total: 0 });
    }
  }, [raffleId, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadParticipants(); }, [loadParticipants]);

  // Kura çekilince kazananlar sekmesi kendiliğinden açılır: yöneticinin
  // baktığı şey artık ödül listesi değil sonuçtur.
  const status = detail?.status;
  useEffect(() => {
    if (status === 'drawn') setTab('winners');
  }, [status]);

  const run = async (key, action, successTitle) => {
    setBusy(key);
    try {
      const result = await action();
      await load();
      await loadParticipants();
      toast({ title: successTitle, description: result });
    } catch (requestError) {
      toast({
        title: 'İşlem tamamlanamadı',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setBusy('');
    }
  };

  const sync = () => run('sync', async () => {
    const { data } = await client.post(`/admin/raffles/${raffleId}/sync`);
    return `${data.users} üyeye ${data.tickets} yeni bilet verildi`;
  }, 'Biletler eşitlendi');

  const draw = () => {
    if (!window.confirm(
      'Kura çekilsin mi? Bu işlem geri alınamaz ve para ödülleri anında '
      + 'kazananların hesabına yazılır.')) return;
    run('draw', async () => {
      const { data } = await client.post(`/admin/raffles/${raffleId}/draw`);
      return `${data.winners.length} kazanan belirlendi`;
    }, 'Kura çekildi');
  };

  const grantTickets = () => {
    if (!grant.username.trim()) return;
    run('grant', async () => {
      const { data } = await client.post(`/admin/raffles/${raffleId}/tickets`, {
        username: grant.username.trim(),
        count: Number(grant.count || 1),
        note: grant.note,
      });
      setGrant({ username: '', count: 1, note: '' });
      return `${data.granted} bilet verildi`;
    }, 'Bilet eklendi');
  };

  const savePrize = () => run('prize', async () => {
    const payload = {
      title: prizeForm.title.trim(),
      reward_type: prizeForm.reward_type,
      amount: Number(prizeForm.amount || 0),
      wager_multiplier: Number(prizeForm.wager_multiplier || 0),
      winner_count: Number(prizeForm.winner_count || 1),
      order: Number(prizeForm.order || 0),
    };
    if (prizeModal.mode === 'new') {
      await client.post(`/admin/raffles/${raffleId}/prizes`, payload);
    } else {
      await client.patch(`/admin/raffles/${raffleId}/prizes/${prizeModal.id}`, payload);
    }
    setPrizeModal(null);
    return payload.title;
  }, 'Ödül kaydedildi');

  const removePrize = (prize) => {
    if (!window.confirm(`${prize.title} ödülü silinsin mi?`)) return;
    run('prize', async () => {
      await client.delete(`/admin/raffles/${raffleId}/prizes/${prize.id}`);
      return prize.title;
    }, 'Ödül silindi');
  };

  const deliver = (winner) => run('deliver', async () => {
    await client.post(
      `/admin/raffles/${raffleId}/winners/${winner.id}/deliver`, { note: '' });
    return `${winner.username} · ${winner.prize_title}`;
  }, 'Teslim işaretlendi');

  const openPrize = (prize) => {
    setPrizeForm(prize
      ? { ...emptyPrize, ...prize }
      : { ...emptyPrize, order: (detail?.prizes || []).length });
    setPrizeModal(prize ? { mode: 'edit', id: prize.id } : { mode: 'new' });
  };

  const pendingDeliveries = useMemo(
    () => (detail?.winners || []).filter((winner) => winner.status !== 'delivered').length,
    [detail],
  );

  if (loading) {
    return (
      <div className="rfl-page">
        <div className="rfl-panel">
          <div className="rfl-state">
            <Loader2 size={24} className="animate-spin" />
            <p>Çekiliş yükleniyor…</p>
          </div>
        </div>
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="rfl-page">
        <div className="rfl-panel">
          <div className="rfl-state">
            <AlertTriangle size={26} />
            <h2>Çekiliş açılamadı</h2>
            <p>{error}</p>
            <button type="button" onClick={onBack} className="admin-btn-secondary">
              <ArrowLeft size={14} /> Listeye dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  const drawn = detail.status === 'drawn';
  const pages = Math.max(1, Math.ceil((participants.total || 0) / pageSize));

  return (
    <div className="rfl-page">
      <header className="rfl-head">
        <button type="button" onClick={onBack} className="rfl-head-icon" title="Listeye dön">
          <ArrowLeft size={19} />
        </button>
        <div className="rfl-head-text">
          <h1>
            {detail.title}
            <span className={`rfl-pill ${STATUS_TONES[detail.status] || 'is-muted'}`}>
              {STATUS_LABELS[detail.status] || detail.status}
            </span>
          </h1>
          <p>
            {money(detail.ticket_amount)} = 1 bilet ·{' '}
            {formatDate(detail.starts_at)} – {formatDate(detail.ends_at)}
            {detail.draw_at ? ` · Kura ilanı ${formatDate(detail.draw_at)}` : ''}
          </p>
        </div>
        <div className="rfl-head-actions">
          {!drawn && (
            <button type="button" onClick={() => onEdit(detail)} className="admin-btn-secondary">
              <Edit3 size={14} /> Düzenle
            </button>
          )}
          {detail.status === 'active' && (
            <button
              type="button"
              onClick={sync}
              disabled={busy === 'sync'}
              className="admin-btn-secondary"
            >
              {busy === 'sync'
                ? <Loader2 size={14} className="animate-spin" />
                : <RefreshCw size={14} />}
              Biletleri Eşitle
            </button>
          )}
          {!drawn && (
            <button
              type="button"
              onClick={draw}
              disabled={busy === 'draw' || (detail.prizes || []).length === 0
                || (detail.ticket_count || 0) === 0}
              className="admin-btn-primary"
              title={(detail.prizes || []).length === 0
                ? 'Önce ödül tanımlayın'
                : ((detail.ticket_count || 0) === 0 ? 'Henüz bilet yok' : 'Kurayı çek')}
            >
              {busy === 'draw'
                ? <Loader2 size={14} className="animate-spin" />
                : <Dices size={14} />}
              Kurayı Çek
            </button>
          )}
        </div>
      </header>

      {!drawn && (detail.prizes || []).length === 0 && (
        <div className="rfl-note is-warn">
          <AlertTriangle size={15} />
          <span className="rfl-note-body">
            Ödül tanımlanmadan kura çekilemez. <b>Ödüller</b> sekmesinden en az
            bir ödül ekleyin.
          </span>
        </div>
      )}

      {drawn && (
        <div className="rfl-note is-info">
          <Info size={15} />
          <span className="rfl-note-body">
            Kura {formatDate(detail.drawn_at)} tarihinde çekildi. Para ödülleri
            kazananların hesabına <b>yazıldı</b>; fiziksel ödüllerin teslimini
            siz işaretlersiniz. Kayıt artık değiştirilemez.
          </span>
        </div>
      )}

      <div className="rfl-stats">
        <Stat icon={Ticket} tone="gold" label="Toplam bilet" value={compact(detail.ticket_count)} />
        <Stat icon={Users} tone="info" label="Katılımcı" value={compact(detail.participant_count)} />
        <Stat
          icon={Gift}
          tone="neutral"
          label={drawn ? 'Ödül' : 'Ödül hakkı'}
          value={compact(drawn ? detail.prize_count : detail.winner_slots)}
          hint={money(detail.prize_pool)}
        />
        <Stat
          icon={Trophy}
          tone={pendingDeliveries > 0 ? 'warn' : 'ok'}
          label="Kazanan"
          value={compact(detail.winner_count)}
          hint={pendingDeliveries > 0 ? `${pendingDeliveries} teslim bekliyor` : ''}
        />
      </div>

      <nav className="rfl-tabs">
        <Tab
          active={tab === 'prizes'}
          onClick={() => setTab('prizes')}
          icon={Gift}
          label="Ödüller"
          count={(detail.prizes || []).length}
        />
        <Tab
          active={tab === 'participants'}
          onClick={() => setTab('participants')}
          icon={Users}
          label="Katılımcılar"
          count={participants.total || 0}
        />
        <Tab
          active={tab === 'winners'}
          onClick={() => setTab('winners')}
          icon={Trophy}
          label="Kazananlar"
          count={(detail.winners || []).length}
          warn={pendingDeliveries > 0}
        />
      </nav>

      {tab === 'prizes' && (
        <section className="rfl-panel">
          <div className="rfl-panel-head">
            <span className="rfl-panel-title"><Gift size={14} /> Ödüller</span>
            <span className="rfl-panel-meta">
              {compact(detail.winner_slots)} kişi · {money(detail.prize_pool)}
            </span>
            {!drawn && (
              <button type="button" className="rfl-mini is-primary" onClick={() => openPrize(null)}>
                <Plus size={13} /> Ödül Ekle
              </button>
            )}
          </div>
          {(detail.prizes || []).length === 0 ? (
            <div className="rfl-state">
              <Gift size={24} />
              <h2>Ödül yok</h2>
              <p>
                Sıra numarası küçük olan ödül önce çekilir. Nakit ödül ana
                bakiyeye çevrimsiz yazılır, bonus çevrim şartına bağlanır,
                fiziksel ödül yalnızca kayıttır.
              </p>
            </div>
          ) : (
            <div className="rfl-prizes">
              {detail.prizes.map((prize, index) => {
                const Icon = PRIZE_ICONS[prize.reward_type] || Coins;
                return (
                  <div className="rfl-prize" key={prize.id}>
                    <span className="rfl-prize-rank">{index + 1}</span>
                    <div className="rfl-prize-text">
                      <div className="rfl-prize-title">
                        {prize.title}
                        <span className={`rfl-pill ${
                          prize.reward_type === 'custom' ? 'is-muted' : 'is-ok'}`}
                        >
                          <Icon size={11} />
                          {PRIZE_LABELS[prize.reward_type] || prize.reward_type}
                        </span>
                      </div>
                      <div className="rfl-prize-sub">
                        {prize.winner_count > 1
                          ? `${prize.winner_count} kişiye`
                          : 'Tek kişiye'}
                        {prize.reward_type === 'bonus' && Number(prize.wager_multiplier) > 0
                          && ` · Çevrim ×${Number(prize.wager_multiplier)}`}
                        {` · Sıra ${prize.order}`}
                      </div>
                    </div>
                    <span className={`rfl-prize-value ${
                      prize.reward_type === 'custom' ? 'is-custom' : ''}`}
                    >
                      {prize.reward_type === 'custom' ? 'Fiziksel' : money(prize.amount)}
                    </span>
                    {!drawn && (
                      <div className="rfl-prize-actions">
                        <button
                          type="button"
                          className="rfl-mini is-icon"
                          title="Düzenle"
                          onClick={() => openPrize(prize)}
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          type="button"
                          className="rfl-mini is-icon is-danger"
                          title="Sil"
                          onClick={() => removePrize(prize)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {tab === 'participants' && (
        <section className="rfl-panel">
          <div className="rfl-toolbar">
            <label className="rfl-search">
              <Search size={14} />
              <input
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(0); }}
                className="admin-input pl-10"
                placeholder="Kullanıcı adı ara…"
              />
            </label>
            <span className="rfl-panel-meta">
              {compact(participants.total || 0)} üye · {compact(detail.ticket_count)} bilet
            </span>
          </div>

          {!drawn && (
            <div className="rfl-grant">
              <label className="rfl-grant-field is-user">
                <span>Kullanıcı adı</span>
                <input
                  value={grant.username}
                  onChange={(event) => setGrant({ ...grant, username: event.target.value })}
                  className="admin-input"
                  placeholder="uye_adi"
                />
              </label>
              <label className="rfl-grant-field is-count">
                <span>Adet</span>
                <input
                  type="number"
                  min="1"
                  value={grant.count}
                  onChange={(event) => setGrant({ ...grant, count: event.target.value })}
                  className="admin-input"
                />
              </label>
              <label className="rfl-grant-field is-note">
                <span>Not</span>
                <input
                  value={grant.note}
                  onChange={(event) => setGrant({ ...grant, note: event.target.value })}
                  className="admin-input"
                  placeholder="Kampanya hediyesi"
                />
              </label>
              <button
                type="button"
                onClick={grantTickets}
                disabled={busy === 'grant' || !grant.username.trim()}
                className="rfl-mini is-primary"
              >
                {busy === 'grant'
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Plus size={13} />}
                Bilet Ver
              </button>
              <p className="rfl-grant-hint">
                Elle verilen bilet yatırımdan doğan hakka <b>eklenir</b>, onu
                düşürmez; eşitleme onu görmez.
              </p>
            </div>
          )}

          {participants.items.length === 0 ? (
            <div className="rfl-state">
              <Users size={24} />
              <h2>Bilet kazanan üye yok</h2>
              <p>
                Üyeler dönem içindeki onaylı yatırımlarıyla bilet kazanır;
                sayfayı açan üyenin bileti kendiliğinden eşitlenir.
              </p>
            </div>
          ) : (
            <>
              <div className="rfl-scroll">
                <table className="rfl-table">
                  <thead>
                    <tr>
                      <th>Üye</th><th>Bilet</th><th>Numara aralığı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.items.map((row) => (
                      <tr key={row.user_id}>
                        <td data-label="Üye">
                          <span className="rfl-user">
                            <span className="rfl-avatar">
                              {(row.username || '?').slice(0, 2).toUpperCase()}
                            </span>
                            {row.username}
                          </span>
                        </td>
                        <td data-label="Bilet">
                          <span className="rfl-count">{compact(row.tickets)}</span>
                        </td>
                        <td data-label="Numara aralığı">
                          <span className="rfl-mono">
                            #{row.first_ticket_no} – #{row.last_ticket_no}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rfl-pager">
                {pages > 1 && (
                  <>
                    <button
                      type="button"
                      className="rfl-mini is-icon"
                      disabled={page === 0}
                      onClick={() => setPage((current) => Math.max(0, current - 1))}
                    >
                      <ChevronLeft size={13} />
                    </button>
                    <span>{page + 1} / {pages}</span>
                    <button
                      type="button"
                      className="rfl-mini is-icon"
                      disabled={page + 1 >= pages}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      <ChevronRight size={13} />
                    </button>
                  </>
                )}
                <PageSizeSelect
                  value={pageSize}
                  onChange={(size) => {
                    setPage(0);
                    setPageSize(size);
                    writePageSize('raffle-participants', size);
                  }}
                />
              </div>
            </>
          )}
        </section>
      )}

      {tab === 'winners' && (
        <section className="rfl-panel">
          <div className="rfl-panel-head">
            <span className="rfl-panel-title"><Trophy size={14} /> Kazananlar</span>
            <span className="rfl-panel-meta">
              {compact((detail.winners || []).length)} kişi
              {pendingDeliveries > 0 && ` · ${pendingDeliveries} teslim bekliyor`}
            </span>
          </div>
          {(detail.winners || []).length === 0 ? (
            <div className="rfl-state">
              <Dices size={24} />
              <h2>Kura henüz çekilmedi</h2>
              <p>
                Kazanan bilet <b>rastgele</b> seçilir ve kura yalnızca bir kez
                döner. Para ödülleri aynı anda hesaba yazılır.
              </p>
            </div>
          ) : (
            <div className="rfl-scroll">
              <table className="rfl-table">
                <thead>
                  <tr>
                    <th>Ödül</th><th>Üye</th><th>Bilet</th>
                    <th>Tutar</th><th>Durum</th><th className="rfl-cell-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.winners.map((winner) => (
                    <tr
                      key={winner.id}
                      className={winner.status !== 'delivered'
                        && winner.reward_type === 'custom' ? 'is-queued' : ''}
                    >
                      <td data-label="Ödül"><b>{winner.prize_title}</b></td>
                      <td data-label="Üye">
                        <span className="rfl-user">
                          <span className="rfl-avatar">
                            {(winner.username || '?').slice(0, 2).toUpperCase()}
                          </span>
                          {winner.username}
                        </span>
                      </td>
                      <td data-label="Bilet">
                        <span className="rfl-mono">#{winner.ticket_no}</span>
                      </td>
                      <td data-label="Tutar">
                        {winner.reward_type === 'custom' ? (
                          <span className="rfl-hint">Fiziksel</span>
                        ) : (
                          <span className="rfl-count">{money(winner.amount)}</span>
                        )}
                        {Number(winner.wager_required) > 0 && (
                          <div className="rfl-hint">
                            Çevrim {money(winner.wager_required)}
                          </div>
                        )}
                      </td>
                      <td data-label="Durum">
                        <span className={`rfl-pill ${
                          winner.status === 'delivered' ? 'is-ok' : 'is-warn'}`}
                        >
                          {winner.status === 'delivered' ? 'Teslim edildi' : 'Teslim bekliyor'}
                        </span>
                      </td>
                      <td data-label="İşlem" className="rfl-cell-right">
                        {winner.status !== 'delivered' ? (
                          <button
                            type="button"
                            onClick={() => deliver(winner)}
                            disabled={busy === 'deliver'}
                            className="rfl-mini"
                          >
                            <CheckCircle2 size={13} /> Teslim edildi
                          </button>
                        ) : (
                          <span className="rfl-hint">
                            {formatDate(winner.delivered_at)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {prizeModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => busy !== 'prize' && setPrizeModal(null)}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div
            className="relative admin-card admin-modal w-full max-w-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><Gift size={19} /></div>
              <div className="min-w-0">
                <h2 className="font-black text-[15px] leading-tight">
                  {prizeModal.mode === 'new' ? 'Yeni Ödül' : 'Ödülü Düzenle'}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Sıra numarası küçük olan ödül önce çekilir
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPrizeModal(null)}
                className="icon-btn view ml-auto"
              >
                <X size={14} />
              </button>
            </div>

            <div className="admin-modal-body">
              <section className="admin-fieldset">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Ödül Adı">
                      <input
                        value={prizeForm.title}
                        onChange={(event) => setPrizeForm({
                          ...prizeForm, title: event.target.value,
                        })}
                        className="admin-input"
                        placeholder="Birincilik Ödülü"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-xs text-slate-400 block mb-1.5">Ödül Türü</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <RewardChoice
                        icon={Coins}
                        title="Nakit"
                        description="Ana bakiyeye yazılır"
                        active={prizeForm.reward_type === 'cash'}
                        onSelect={() => setPrizeForm({ ...prizeForm, reward_type: 'cash' })}
                      />
                      <RewardChoice
                        icon={Sparkles}
                        title="Bonus"
                        description="Çevrim şartıyla"
                        active={prizeForm.reward_type === 'bonus'}
                        onSelect={() => setPrizeForm({ ...prizeForm, reward_type: 'bonus' })}
                      />
                      <RewardChoice
                        icon={Package}
                        title="Fiziksel"
                        description="Para hareketi yok"
                        active={prizeForm.reward_type === 'custom'}
                        onSelect={() => setPrizeForm({ ...prizeForm, reward_type: 'custom' })}
                      />
                    </div>
                  </div>
                  {prizeForm.reward_type !== 'custom' && (
                    <Field label="Tutar">
                      <Affix text="₺">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={prizeForm.amount}
                          onChange={(event) => setPrizeForm({
                            ...prizeForm, amount: event.target.value,
                          })}
                          className="admin-input pr-10"
                        />
                      </Affix>
                    </Field>
                  )}
                  {prizeForm.reward_type === 'bonus' && (
                    <Field label="Çevrim Şartı" hint="0 = çevrimsiz">
                      <Affix text="×" side="left">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={prizeForm.wager_multiplier}
                          onChange={(event) => setPrizeForm({
                            ...prizeForm, wager_multiplier: event.target.value,
                          })}
                          className="admin-input pl-10"
                        />
                      </Affix>
                    </Field>
                  )}
                  <Field label="Kazanan Sayısı" hint="Aynı ödülden kaç kişi kazanacak.">
                    <Affix text="kişi">
                      <input
                        type="number"
                        min="1"
                        value={prizeForm.winner_count}
                        onChange={(event) => setPrizeForm({
                          ...prizeForm, winner_count: event.target.value,
                        })}
                        className="admin-input pr-10"
                      />
                    </Affix>
                  </Field>
                  <Field label="Sıra" hint="Küçük numara önce çekilir (0 = büyük ödül).">
                    <input
                      type="number"
                      min="0"
                      value={prizeForm.order}
                      onChange={(event) => setPrizeForm({
                        ...prizeForm, order: event.target.value,
                      })}
                      className="admin-input"
                    />
                  </Field>
                </div>
              </section>
            </div>

            <div className="admin-modal-foot">
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPrizeModal(null)}
                  className="admin-btn-secondary"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  disabled={busy === 'prize' || !prizeForm.title.trim()}
                  onClick={savePrize}
                  className="admin-btn-primary"
                >
                  {busy === 'prize' && <Loader2 size={14} className="animate-spin" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, hint, tone = 'neutral' }) => (
  <div className={`rfl-stat tone-${tone} ${tone === 'neutral' ? '' : 'is-toned'}`}>
    <span className="rfl-stat-label"><Icon size={12} /> {label}</span>
    <span className="rfl-stat-value">{value}</span>
    {hint && <span className="rfl-stat-hint">{hint}</span>}
  </div>
);

const Tab = ({ active, onClick, icon: Icon, label, count, warn }) => (
  <button type="button" className={`rfl-tab ${active ? 'is-active' : ''}`} onClick={onClick}>
    <Icon size={13} /> {label}
    <span className={`rfl-tab-count ${warn && !active ? 'is-warn' : ''}`}>{count}</span>
  </button>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 block mb-1.5">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">{hint}</span>}
  </label>
);

// Girişin içinde duran birim etiketi. Dolgu .admin-input.pl-10 / .pr-10 ile
// verilir — Tailwind'in padding utility'leri .admin-input tarafından eziliyor.
const Affix = ({ text, side = 'right', children }) => (
  <span className={`admin-affix block ${side === 'left' ? 'affix-left' : ''}`}>
    {children}
    <span className="admin-affix-text">{text}</span>
  </span>
);

const RewardChoice = ({ icon: Icon, title, description, active, onSelect }) => (
  <button type="button" onClick={onSelect} className={`admin-choice ${active ? 'active' : ''}`}>
    <span className="admin-choice-icon"><Icon size={16} /></span>
    <span className="min-w-0">
      <span className="block text-sm font-bold">{title}</span>
      <span className="block text-[11px] text-slate-500 mt-0.5">{description}</span>
    </span>
  </button>
);

const StatusChoice = ({ title, description, active, onSelect }) => (
  <button type="button" onClick={onSelect} className={`admin-choice ${active ? 'active' : ''}`}>
    <span className="min-w-0">
      <span className="block text-sm font-bold">{title}</span>
      <span className="block text-[11px] text-slate-500 mt-0.5">{description}</span>
    </span>
  </button>
);

const SwitchRow = ({ icon: Icon, label, description, checked, onChange }) => (
  <label className={`admin-switch ${checked ? 'on' : ''}`}>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span className="admin-switch-track" aria-hidden="true" />
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={13} /> {label}</span>
      <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">{description}</span>
    </span>
  </label>
);

export default Raffles;
